<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="modal-container" @click.stop>
      <!-- Header Premium -->
      <div class="modal-header">
        <div class="header-content">
          <div class="icon-wrapper">
            <i class="fas fa-receipt"></i>
          </div>
          <div class="header-text">
            <h3>Détail de l'Encaissement</h3>
            <p>Informations complètes de la transaction</p>
          </div>
        </div>
        <button @click="$emit('close')" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Contenu -->
      <div class="modal-body">
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Chargement des détails...</p>
        </div>

        <div v-else-if="encaissement" class="encaissement-details">
          <!-- Carte Héro avec montant -->
          <div class="hero-card">
            <div class="montant-section">
              <div class="montant-principal">{{ formatCurrency(encaissement.montant) }}</div>
              <div class="mode-paiement">{{ getModePaiementLabel(encaissement.modePaiement) }}</div>
            </div>
            <div class="date-section">
              <div class="date-principale">{{ formatDate(encaissement.dateEncaissement) }}</div>
              <div class="horaires">{{ encaissement.heureDebut }} - {{ encaissement.heureFin }}</div>
            </div>
          </div>

          <!-- Informations Client -->
          <div class="info-section" :class="{ 'section-collapsed': !expandedSections.client }">
            <h4 @click="toggleSection('client')">
              <i class="fas fa-user"></i> Informations Client
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </h4>
            <div v-show="expandedSections.client" class="info-grid">
              <div class="info-item">
                <span class="label">Nom complet :</span>
                <span class="value">
                  {{ encaissement.client?.nomComplet || 'Non renseigné' }}
                </span>
              </div>
              <div class="info-item">
                <span class="label">Téléphone :</span>
                <span class="value">{{ encaissement.client?.telephone || 'Non renseigné' }}</span>
              </div>
              <div class="info-item">
                <span class="label">Adresse :</span>
                <div class="value address-formatted">
                  {{ formatAddress(encaissement.client?.adresse) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Détails Intervention -->
          <div class="info-section" :class="{ 'section-collapsed': !expandedSections.intervention }">
            <h4 @click="toggleSection('intervention')">
              <i class="fas fa-tools"></i> Détails Opérations
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </h4>
            <div v-show="expandedSections.intervention" class="info-grid">
              <div class="info-item">
                <span class="label">Technicien :</span>
                <span class="value">
                  <i class="fas fa-user-cog"></i>
                  {{ encaissement.technicien?.prenom || '' }} {{ encaissement.technicien?.nom || 'Non assigné' }}
                </span>
              </div>
              <div v-if="encaissement.imeiIccidScanne?.length > 0" class="info-item full-width">
                <span class="label">Produits installés :</span>
                <div class="imei-list">
                  <div 
                    v-for="(item, index) in encaissement.imeiIccidScanne" 
                    :key="item._id || index"
                    class="imei-item"
                  >
                    <div class="imei-info">
                      <span class="imei-type">{{ item.type }}</span>
                      <span class="imei-value">{{ item.valeur }}</span>
                    </div>
                    <div class="product-info">
                      <span v-if="item.produit?.designation" class="product-name">
                        {{ item.produit.designation }}
                      </span>
                      <span v-else class="product-name">
                        {{ getTypeAppareil(item.type) }}
                      </span>
                      <span v-if="item.produit?.categorie" class="product-category">
                        {{ getCategorieLabel(item.produit.categorie) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="encaissement.commentairesTechnicien" class="info-item">
                <span class="label">Commentaires technicien :</span>
                <span class="value">{{ encaissement.commentairesTechnicien }}</span>
              </div>
            </div>
          </div>

          <!-- Détails Paiement -->
          <div class="info-section" :class="{ 'section-collapsed': !expandedSections.paiement }">
            <h4 @click="toggleSection('paiement')">
              <i class="fas fa-credit-card"></i> Détails Paiement
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </h4>
            <div v-show="expandedSections.paiement" class="info-grid">
              <div class="info-item">
                <span class="label">Prix RDV :</span>
                <span class="value prix-rdv">
                  <i class="fas fa-hand-holding-usd"></i>
                  {{ formatCurrency(encaissement.prixRdv) }}
                </span>
              </div>
              <div class="info-item">
                <span class="label">Montant encaissé :</span>
                <span class="value montant-encaisse">
                  <i class="fas fa-coins"></i>
                  {{ formatCurrency(encaissement.montant) }}
                </span>
              </div>
              <div class="info-item">
                <span class="label">Mode de paiement :</span>
                <span class="value">{{ getModePaiementLabel(encaissement.modePaiement) }}</span>
              </div>
              <div v-if="encaissement.commentaires" class="info-item">
                <span class="label">Commentaires :</span>
                <span class="value">{{ encaissement.commentaires }}</span>
              </div>
            </div>
          </div>

          <!-- Photo Justificatif -->
          <div v-if="encaissement.photoJustificatif" class="info-section" :class="{ 'section-collapsed': !expandedSections.justificatif }">
            <h4 @click="toggleSection('justificatif')">
              <i class="fas fa-image"></i> Justificatif
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </h4>
            <div v-show="expandedSections.justificatif" class="photo-container">
              <img 
                :src="encaissement.photoJustificatif" 
                alt="Justificatif de paiement"
                class="photo-justificatif"
                @click="openPhotoModal"
              >
              <p class="photo-hint">Cliquez pour agrandir</p>
            </div>
          </div>

          <!-- Signature Client -->
          <div v-if="encaissement.signatureClient" class="info-section" :class="{ 'section-collapsed': !expandedSections.signature }">
            <h4 @click="toggleSection('signature')">
              <i class="fas fa-signature"></i> Signature Client
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </h4>
            <div v-show="expandedSections.signature" class="signature-container">
              <img 
                :src="encaissement.signatureClient.data" 
                alt="Signature client"
                class="signature-image"
              >
              <p class="signature-name">{{ encaissement.signatureClient.nomSignataire }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button @click="$emit('close')" class="btn-secondary">
          <i class="fas fa-times"></i>
          Fermer
        </button>
        <button @click="exportRecuPDF" class="btn-export-pdf">
          <i class="fas fa-file-pdf"></i>
          Générer reçu
        </button>
      </div>
    </div>

    <!-- Modal Photo -->
    <div v-if="showPhotoModal" class="photo-modal-overlay" @click="closePhotoModal">
      <div class="photo-modal-container" @click.stop>
        <button @click="closePhotoModal" class="photo-close-btn">
          <i class="fas fa-times"></i>
        </button>
        <img 
          :src="encaissement?.photoJustificatif" 
          alt="Justificatif de paiement"
          class="photo-modal-image"
        >
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useToast } from 'vue-toastification'
import { useStore } from 'vuex'
import jsPDF from 'jspdf'
import QRCode from 'qrcode'
import { RECU_BASE_URL } from '@/config/api'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Capacitor } from '@capacitor/core'

// Fonction utilitaire pour télécharger PDF sur mobile
async function downloadPDFMobile(doc, fileName) {
  try {
    const isNative = Capacitor.isNativePlatform()
    if (isNative) {
      const base64Data = doc.output('datauristring').split(',')[1]
      const result = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Documents,
        recursive: true
      })
      return { success: true, message: `Reçu téléchargé dans Documents/${fileName}`, path: result.uri }
    } else {
      doc.save(fileName)
      return { success: true, message: 'Reçu téléchargé avec succès' }
    }
  } catch (error) {
    console.error('Erreur téléchargement PDF:', error)
    try {
      const pdfBlob = doc.output('blob')
      const blobUrl = URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000)
      return { success: true, message: 'Reçu téléchargé' }
    } catch (e) {
      return { success: false, message: 'Erreur lors du téléchargement' }
    }
  }
}

export default {
  name: 'EncaissementDetailModal',
  props: {
    encaissementId: String
  },
  emits: ['close'],
  setup(props) {
    const toast = useToast()
    const store = useStore()
    const loading = ref(false)
    const encaissement = ref(null)
    const showPhotoModal = ref(false)
    
    // Récupérer le rôle depuis le store Vuex
    const userRole = computed(() => store.getters['auth/userRole'])

    // État des sections repliables (toutes fermées par défaut)
    const expandedSections = ref({
      client: false,
      intervention: false,
      paiement: false,
      justificatif: false,
      signature: false
    })

    // Fonction pour basculer l'état d'une section
    const toggleSection = (section) => {
      expandedSections.value[section] = !expandedSections.value[section]
    }

    const loadEncaissementDetail = async () => {
      loading.value = true
      try {
        const token = localStorage.getItem('token')
        const role = userRole.value
        
        console.log('🔍 Debug Frontend - Modal chargé')
        console.log('🔍 Debug Frontend - encaissementId:', props.encaissementId)
        console.log('🔍 Debug Frontend - Rôle utilisateur (depuis Vuex):', role)
        
        // Choisir l'endpoint selon le rôle
        let endpoint
        if (role === 'Technicien') {
          endpoint = `/api/caissier/encaissements/${props.encaissementId}`
        } else {
          // Pour tous les autres rôles (Admin, Financier, Planning, Commercial, Magasinier)
          endpoint = `/api/finance/encaissements/${props.encaissementId}`
        }
        
        console.log('🔍 Debug Frontend - Endpoint utilisé:', endpoint)
        console.log('🔍 Debug Frontend - Token présent:', !!token)
        
        const response = await fetch(endpoint, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        
        if (response.ok) {
          const data = await response.json()
          console.log('🔍 Debug Frontend - Données reçues:', data)
          console.log('🔍 Debug Frontend - data.encaissement:', data.encaissement)
          console.log('🔍 Debug Frontend - Structure complète:', JSON.stringify(data, null, 2))
          
          encaissement.value = data.encaissement || data
          
          if (!encaissement.value) {
            console.error('🔍 Debug Frontend - Aucune donnée d\'encaissement dans la réponse')
            toast.error('Aucune donnée d\'encaissement trouvée')
          } else {
            console.log('🔍 Debug Frontend - Encaissement final:', encaissement.value)
            console.log('🔍 Debug Frontend - Client:', encaissement.value.client)
            console.log('🔍 Debug Frontend - Client nom:', encaissement.value.client?.nom)
            console.log('🔍 Debug Frontend - Client prenom:', encaissement.value.client?.prenom)
            console.log('🔍 Debug Frontend - IMEI/ICCID:', encaissement.value.imeiIccidScanne)
            if (encaissement.value.imeiIccidScanne?.length > 0) {
              console.log('🔍 Debug Frontend - Premier produit:', encaissement.value.imeiIccidScanne[0].produit)
            }
          }
        } else {
          const errorData = await response.text()
          console.error('🔍 Debug - Erreur response:', response.status, response.statusText)
          console.error('🔍 Debug - Détail erreur:', errorData)
          
          if (response.status === 404) {
            toast.error('Encaissement non trouvé')
          } else if (response.status === 403) {
            toast.error('Accès non autorisé')
          } else {
            toast.error('Erreur lors du chargement des détails')
          }
        }
      } catch (error) {
        console.error('🔍 Debug - Erreur:', error)
        toast.error('Erreur lors du chargement des détails')
      } finally {
        loading.value = false
      }
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND'
      }).format(amount || 0)
    }

    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const getModePaiementLabel = (mode) => {
      const modes = {
        'especes': '💵 Espèces',
        'cheque': '📝 Chèque',
        'carte_bancaire': '💳 Carte Bancaire',
        'virement': '🏦 Virement',
        'prelevement': '📋 Prélèvement'
      }
      return modes[mode] || mode
    }

    const formatAddress = (address) => {
      if (!address) return 'Non renseignée'
      
      // Si c'est déjà une chaîne, la retourner
      if (typeof address === 'string') {
        return address
      }
      
      // Si c'est un objet, le formater
      if (typeof address === 'object') {
        const parts = []
        
        if (address.rue) parts.push(address.rue)
        if (address.ville) parts.push(address.ville)
        if (address.codePostal) parts.push(address.codePostal)
        if (address.region) parts.push(address.region)
        
        return parts.length > 0 ? parts.join(', ') : 'Adresse incomplète'
      }
      
      return 'Format d\'adresse invalide'
    }

    const getCategorieLabel = (categorie) => {
      if (!categorie) return 'Non renseigné'
      
      const labels = {
        'gps': '📡 GPS',
        'sim': '📱 SIM',
        'accessoire': '🔌 Accessoire',
        'autre': '📦 Autre'
      }
      return labels[categorie] || categorie
    }

    const getTypeAppareil = (type) => {
      if (!type) return 'Materiel GPS'
      
      const typeUpper = type.toUpperCase()
      if (typeUpper === 'IMEI') {
        return 'GPS'
      } else if (typeUpper === 'ICCID') {
        return 'SIM'
      }
      return 'Materiel GPS'
    }

    const openPhotoModal = () => {
      showPhotoModal.value = true
    }

    const closePhotoModal = () => {
      showPhotoModal.value = false
    }

    const drawRecuContent = (doc, pageWidth, centerX, recuId, qrCodeDataUrl = null) => {
      let y = 10

        // ========== LOGO AMANA EN HAUT À DROITE ==========
        const logoSize = 18
        const logoX = pageWidth - logoSize - 5
        const logoY = 5
        try {
          const logoImg = new Image()
          logoImg.src = require('@/assets/amana1.jpeg')
          doc.addImage(logoImg, 'JPEG', logoX, logoY, logoSize, logoSize)
        } catch (e) {
          console.log('Logo non chargé:', e)
        }

        // ========== EN-TÊTE AMANA ==========
        doc.setFontSize(16)
        doc.setFont('helvetica', 'bold')
        doc.text('AMANA GPS', 8, y) // Aligné à gauche pour laisser place au QR
        y += 5
        
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.text('MANAGEMENT SUITE', 8, y)
        y += 4
        doc.text('9 AV Bardo Hbib,Bourgiba Tunis', 8, y)
        y += 4
        doc.text('Tel: +216 96 677 399', 8, y)
        y += 6

        // Ligne de séparation
        doc.setDrawColor(0, 0, 0)
        doc.setLineWidth(0.3)
        doc.line(5, y, pageWidth - 5, y)
        y += 5

        // ========== TITRE ==========
        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        doc.text('REÇU DE CAISSE', centerX, y, { align: 'center' })
        y += 6

        // Ligne de séparation
        doc.line(5, y, pageWidth - 5, y)
        y += 6

        // ========== NUMÉRO ET DATE ==========
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.text(`N° ${recuId}`, centerX, y, { align: 'center' })
        y += 4
        doc.text(`Date: ${formatDate(encaissement.value.dateEncaissement)}`, centerX, y, { align: 'center' })
        y += 6

        // Ligne de séparation
        doc.line(5, y, pageWidth - 5, y)
        y += 6

        // ========== INFORMATIONS CLIENT ==========
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.text(`Client: ${encaissement.value.client?.nomComplet || 'Non renseigné'}`, 8, y)
        y += 4
        if (encaissement.value.client?.telephone) {
          doc.text(`Tél: ${encaissement.value.client.telephone}`, 8, y)
          y += 4
        }
       
        doc.text(`Date: ${formatDate(encaissement.value.dateEncaissement)}`, 8, y)
        y += 4
        
        // Adresse
        const adresseFormatee = formatAddress(encaissement.value.client?.adresse)
        doc.text(`Adresse: ${adresseFormatee}`, 8, y)
        y += 6
 // Code Log
        const codeLog = encaissement.value.client?.codeLog || encaissement.value.codeLog || ''
        if (codeLog) {
          doc.text(`Login: ${codeLog}`, 8, y)
          y += 4
        }
        // Ligne de séparation
        doc.line(5, y, pageWidth - 5, y)
        y += 6

        // ========== DÉTAILS PRODUITS ==========
        doc.setFontSize(8)

        // IMEI/ICCID installés
        if (encaissement.value.imeiIccidScanne?.length > 0) {
          encaissement.value.imeiIccidScanne.forEach(item => {
            // Afficher la désignation du produit si disponible, sinon déterminer par le type
            let designation = ''
            if (item.produit?.designation) {
              designation = item.produit.designation
            } else {
              // Si pas de produit, déterminer par le type de code
              if (item.type) {
                const typeUpper = item.type.toUpperCase()
                if (typeUpper === 'IMEI') {
                  designation = 'GPS'
                } else if (typeUpper === 'ICCID') {
                  designation = 'SIM'
                } else {
                  designation = 'Materiel GPS'
                }
              } else {
                designation = 'Materiel GPS'
              }
            }
            
            // Afficher "Designation:" et le nom du produit sur la même ligne
              doc.setFont('helvetica', 'normal')
            doc.text('Designation:', 8, y)
            doc.setFont('helvetica', 'bold')
            doc.text(designation, 30, y)
              y += 4
          })
            } else {
          doc.setFont('helvetica', 'normal')
          doc.text('Designation:', 8, y)
          doc.setFont('helvetica', 'bold')
          doc.text('Service d\'intervention', 30, y)
          y += 4
        }

        // Fournisseur
        doc.text(`Technicien: ${encaissement.value.technicien?.prenom || ''} ${encaissement.value.technicien?.nom || ''}`, 8, y)
        y += 4

        // Prix RDV
        if (encaissement.value.prixRdv) {
          doc.setFont('helvetica', 'normal')
          doc.text('Prix RDV:', 8, y)
        doc.setFont('helvetica', 'bold')
          const prixRdvFormate = formatCurrency(encaissement.value.prixRdv).replace('TND', 'DT')
          doc.text(prixRdvFormate, pageWidth - 8, y, { align: 'right' })
          y += 4
        }

        // Mode de paiement
        doc.setFont('helvetica', 'normal')
        const modePaiement = getModePaiementLabel(encaissement.value.modePaiement).replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '').trim()
        doc.text(`Paiement: ${modePaiement}`, 8, y)
        y += 4
        
        // Statut
        doc.text('Statut: Validée', 8, y)
        y += 6

        // Ligne de séparation
        doc.line(5, y, pageWidth - 5, y)
        y += 6

        // ========== MONTANT TOTAL ==========
        doc.setFontSize(11)
        doc.setFont('helvetica', 'bold')
        const montantFormate = formatCurrency(encaissement.value.montant).replace('TND', 'DT')
        doc.text('MONTANT ENCAISSE:', 8, y)
        doc.text(montantFormate, pageWidth - 8, y, { align: 'right' })
        y += 6

        // Double ligne de séparation
        doc.setLineWidth(0.5)
        doc.line(5, y, pageWidth - 5, y)
        y += 1
        doc.line(5, y, pageWidth - 5, y)
        y += 6

        // ========== FOOTER ==========
        doc.setFontSize(7)
        doc.setFont('helvetica', 'italic')
        doc.text('Merci de votre confiance', centerX, y, { align: 'center' })
        y += 4
        doc.setFont('helvetica', 'normal')
        doc.text('Amana Management Suite', centerX, y, { align: 'center' })
        y += 8

        // ========== CODE-BARRES (simulé) ==========
        // Dessiner des lignes verticales pour simuler un code-barres
        const barcodeY = y
        const barcodeWidth = 50
        const barcodeStartX = (pageWidth - barcodeWidth) / 2
        
        doc.setLineWidth(0.5)
        for (let i = 0; i < 30; i++) {
          const x = barcodeStartX + (i * barcodeWidth / 30)
          const height = Math.random() > 0.5 ? 8 : 6
          doc.line(x, barcodeY, x, barcodeY + height)
        }
        
        y += 10
        doc.setFontSize(7)
        doc.text(recuId, centerX, y, { align: 'center' })
        y += 8
        
        // QR Code en bas du reçu
        if (qrCodeDataUrl) {
          const qrSizeBottom = 25
          const qrXBottom = (pageWidth - qrSizeBottom) / 2
          doc.addImage(qrCodeDataUrl, 'PNG', qrXBottom, y, qrSizeBottom, qrSizeBottom)
          y += qrSizeBottom + 3
          doc.setFontSize(6)
          doc.setTextColor(100, 100, 100)
          doc.text('Scannez pour télécharger le reçu', centerX, y, { align: 'center' })
          y += 5
        }
      
      return y
    }

    const exportRecuPDF = async () => {
      if (!encaissement.value) {
        toast.error('Aucune donnée à exporter')
        return
      }

      try {
        const pageWidth = 80
        const centerX = pageWidth / 2
        const recuId = encaissement.value._id?.substring(encaissement.value._id.length - 8).toUpperCase() || 'XXXXXXXX'
        
        // Générer le QR Code avec l'URL du reçu (utiliser le domaine de production)
        const qrCodeUrl = `${RECU_BASE_URL}/recu/${encaissement.value._id}`
        let qrCodeDataUrl = null
        try {
          qrCodeDataUrl = await QRCode.toDataURL(qrCodeUrl, {
            width: 100,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#ffffff'
            },
            errorCorrectionLevel: 'M'
          })
        } catch (qrError) {
          console.warn('⚠️ Erreur génération QR Code:', qrError)
        }
        
        // Créer un document temporaire pour calculer la hauteur
        let tempDoc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [80, 297]
        })
        
        const finalHeight = drawRecuContent(tempDoc, pageWidth, centerX, recuId, qrCodeDataUrl)
        
        // Créer le document final avec la hauteur exacte
        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [80, finalHeight]
        })
        
        // Dessiner le contenu final
        drawRecuContent(doc, pageWidth, centerX, recuId, qrCodeDataUrl)

        // Télécharger le PDF avec Capacitor Filesystem (compatible mobile)
        const fileName = `recu_caisse_${recuId}.pdf`
        const result = await downloadPDFMobile(doc, fileName)
        
        if (result.success) {
          toast.success(`✅ ${result.message}`)
        } else {
          toast.error(result.message)
        }
      } catch (error) {
        console.error('❌ Erreur export PDF:', error)
        toast.error('Erreur lors de l\'export du reçu')
      }
    }

    onMounted(() => {
      console.log('🔍 Debug Frontend - Modal monté, encaissementId:', props.encaissementId)
      if (props.encaissementId) {
        loadEncaissementDetail()
      } else {
        console.error('❌ Debug Frontend - Aucun encaissementId fourni!')
      }
    })

    return {
      loading,
      encaissement,
      showPhotoModal,
      formatCurrency,
      formatDate,
      getModePaiementLabel,
      formatAddress,
      getCategorieLabel,
      getTypeAppareil,
      openPhotoModal,
      closePhotoModal,
      exportRecuPDF,
      expandedSections,
      toggleSection
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.modal-container {
  background: white;
  border-radius: 20px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
}

/* Header */
.modal-header {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  padding: 2rem;
  border-radius: 20px 20px 0 0;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  position: relative;
  z-index: 1;
}

.icon-wrapper {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  font-size: 1.8rem;
}

.header-text h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.8rem;
  font-weight: 700;
}

.header-text p {
  margin: 0;
  opacity: 0.9;
}

.close-btn {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  z-index: 2;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

/* Body */
.modal-body {
  padding: 2rem;
}

.loading-state {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 27, 183, 0.1);
  border-left: 4px solid var(--primary-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Hero Card */
.hero-card {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05), rgba(0, 70, 255, 0.03));
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(0, 27, 183, 0.1);
}

.montant-principal {
  font-size: 2.5rem;
  font-weight: 700;
  color: #10b981;
  margin-bottom: 0.5rem;
}

.mode-paiement {
  font-size: 1.1rem;
  color: var(--primary-blue);
  font-weight: 600;
}

.date-principale {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--primary-blue);
  text-align: right;
  margin-bottom: 0.5rem;
}

.horaires {
  color: #6c757d;
  text-align: right;
}

/* Info Sections */
.info-section {
  background: rgba(248, 250, 252, 0.5);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(0, 27, 183, 0.05);
}

.info-section h4 {
  color: var(--primary-blue);
  margin: 0 0 1rem 0;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
  position: relative;
  justify-content: space-between;
}

.section-toggle-icon {
  color: var(--primary-blue);
  font-size: 1rem;
  transition: transform 0.3s ease;
  margin-left: auto;
}

.section-collapsed .section-toggle-icon {
  transform: rotate(-90deg);
}

.info-grid,
.photo-container,
.signature-container {
  overflow: hidden;
  transition: max-height 0.4s ease-out;
}

.info-grid {
  display: grid;
  gap: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.info-item.full-width {
  flex-direction: column;
  align-items: stretch;
}

.label {
  font-weight: 600;
  color: #6c757d;
  min-width: 150px;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
  font-size: 0.9rem;
}

.value {
  flex: 1;
  text-align: right;
  color: #1a202c;
  font-weight: 500;
}

.value.prix-rdv {
  color: #2563eb;
  font-weight: 700;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.value.prix-rdv i {
  color: #3b82f6;
}

.value.montant-encaisse {
  color: #10b981;
  font-weight: 700;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.value.montant-encaisse i {
  color: #10b981;
}

.address-formatted {
  line-height: 1.5;
  word-break: break-word;
}

/* IMEI List */
.imei-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
}

.imei-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: white;
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid rgba(0, 27, 183, 0.1);
  transition: all 0.3s ease;
}

.imei-item:hover {
  border-color: rgba(0, 27, 183, 0.3);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.1);
}

.imei-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.imei-type {
  font-weight: 600;
  color: var(--primary-blue);
  font-size: 0.85rem;
  background: rgba(0, 27, 183, 0.1);
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
}

.imei-value {
  font-family: monospace;
  color: #1a202c;
  font-weight: 500;
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(0, 27, 183, 0.05);
}

.product-category {
  font-size: 0.85rem;
  color: #10b981;
  font-weight: 600;
}

.product-name {
  font-size: 0.9rem;
  color: #6c757d;
  font-style: italic;
}

.product-info-placeholder {
  padding-top: 0.5rem;
  border-top: 1px solid rgba(0, 27, 183, 0.05);
  text-align: center;
}

.product-info-placeholder .text-muted {
  font-size: 0.85rem;
}

/* Photo et Signature */
.photo-container, .signature-container {
  text-align: center;
}

.photo-justificatif {
  max-width: 100%;
  max-height: 300px;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.3s ease;
  border: 2px solid rgba(0, 27, 183, 0.1);
}

.photo-justificatif:hover {
  transform: scale(1.02);
}

.photo-hint {
  margin: 0.5rem 0 0 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.signature-image {
  max-width: 300px;
  max-height: 150px;
  border: 2px solid rgba(0, 27, 183, 0.1);
  border-radius: 8px;
  background: white;
}

.signature-name {
  margin: 0.5rem 0 0 0;
  color: var(--primary-blue);
  font-weight: 600;
}

/* Footer */
.modal-footer {
  padding: 1.5rem 2rem;
  border-top: 1px solid rgba(0, 27, 183, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn-secondary {
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-secondary:hover {
  background: #5a6268;
  transform: translateY(-2px);
}

.btn-export-pdf {
  background: linear-gradient(135deg, #dc2626, #ef4444);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.btn-export-pdf:hover {
  background: linear-gradient(135deg, #b91c1c, #dc2626);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(220, 38, 38, 0.4);
}

/* Photo Modal */
.photo-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.photo-modal-container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.photo-modal-image {
  max-width: 100%;
  max-height: 100%;
  border-radius: 8px;
}

.photo-close-btn {
  position: absolute;
  top: -50px;
  right: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  backdrop-filter: blur(10px);
}

/* Responsive */
@media (max-width: 768px) {
  .modal-container {
    width: 95% !important;
    max-width: 100% !important;
    max-height: 85vh !important;
    margin: 1rem;
    border-radius: 16px;
  }
  
  .modal-header {
    padding: 1rem !important;
    border-radius: 16px 16px 0 0;
  }
  
  .modal-header h3 {
    font-size: 1.1rem !important;
  }
  
  .modal-header p {
    font-size: 0.8rem !important;
  }
  
  .header-icon {
    width: 40px !important;
    height: 40px !important;
    font-size: 1.2rem !important;
  }
  
  .modal-body {
    padding: 1rem !important;
  }
  
  .hero-card {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
    padding: 1rem !important;
  }
  
  .montant-principal {
    font-size: 1.5rem !important;
  }
  
  .date-principale, .horaires {
    text-align: center;
    font-size: 0.9rem !important;
  }
  
  .info-section {
    padding: 0.75rem !important;
    margin-bottom: 0.75rem !important;
  }
  
  .info-section h4 {
    font-size: 0.95rem !important;
    margin-bottom: 0.75rem !important;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    padding: 0.5rem 0 !important;
  }
  
  .info-item .label {
    font-size: 0.75rem !important;
  }
  
  .info-item .value {
    text-align: left;
    font-size: 0.85rem !important;
  }
  
  .modal-footer {
    padding: 1rem !important;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .modal-footer button {
    width: 100% !important;
    padding: 0.75rem !important;
    font-size: 0.9rem !important;
  }
  
  .close-btn {
    width: 32px !important;
    height: 32px !important;
    font-size: 1rem !important;
  }
}

@media (max-width: 480px) {
  .modal-container {
    width: 100% !important;
    max-height: 90vh !important;
    margin: 0.5rem;
    border-radius: 12px;
  }
  
  .modal-header {
    padding: 0.75rem !important;
  }
  
  .modal-header h3 {
    font-size: 1rem !important;
  }
  
  .modal-body {
    padding: 0.75rem !important;
  }
  
  .hero-card {
    padding: 0.75rem !important;
  }
  
  .montant-principal {
    font-size: 1.3rem !important;
  }
  
  .info-section {
    padding: 0.5rem !important;
  }
}
</style>
